import { MouseEvent as ReactMouseEvent, useCallback, useRef, useState } from 'react';
import styled from 'styled-components';
import { colors } from '@karnott/colors';
import { KIcon, MenuIcon } from '@karnott/icons';
import { Severity, msDuration } from '@karnott/theme';
import { ActionMenuPopover } from './ActionMenuPopover';

const MenuButton = styled.button<{
  opened: boolean;
}>`
  all: unset;
  position: relative;
  cursor: default;
  svg {
    opacity: ${({ opened }) => (opened ? 1 : 0.6)};
    transition: opacity ${msDuration('short')} linear;
  }
  &:not([disabled]) {
    cursor: pointer;
    &:hover svg {
      opacity: 1;
    }
  }
`;

export type MenuPosition = 'left' | 'right';

export type ActionMenuItem = {
  label: string;
  icon?: KIcon;
  action?: () => void;
  severity?: Severity;
};

type Props = {
  /** Items of the action menu */
  items?: ActionMenuItem[];
  /** The position where the menu opens */
  position?: MenuPosition;
  /** Whether or not the menu is disabled */
  disabled?: boolean;
  /** Color of the menu icon */
  color?: string;
  /** Called when the menu opens */
  onOpen?: () => void;
  /** Called when the menu closes */
  onClose?: () => void;
};

/** A menu with actions inside. */
export function ActionMenu({
  items = [],
  position = 'left',
  disabled = false,
  color = colors('black'),
  onOpen = () => {},
  onClose = () => {},
}: Props) {
  const [opened, setOpened] = useState(false);

  const buttonRef = useRef<HTMLButtonElement>(null);

  const close = useCallback(() => {
    setOpened(false);
    onClose();
  }, [onClose]);

  const open = useCallback(() => {
    setOpened(true);
    onOpen();
  }, [onOpen]);

  const onClick = useCallback(
    (e: ReactMouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (items.length === 0) return;

      if (opened) {
        close();
      } else {
        open();
      }
    },
    [close, items.length, open, opened],
  );

  return (
    <>
      <MenuButton onClick={onClick} opened={opened} disabled={disabled} ref={buttonRef} data-testid="action-menu">
        <MenuIcon color={color} />
      </MenuButton>
      <ActionMenuPopover
        triggerRef={buttonRef}
        items={items}
        opened={opened}
        open={open}
        close={close}
        position={position}
      />
    </>
  );
}
